import { addWeeks, isAfter } from "date-fns";
import { z } from "zod";

const PROMPT_STATE_STORAGE_KEY_PREFIX = "passkeySetupPromptState_";
const PENDING_PROMPT_STORAGE_KEY = "pendingPasskeySetupPromptUserId";
const DECLINED_CEREMONY_STORAGE_KEY_PREFIX = "passkeyCeremonyDeclined_";
const PROMPT_COOLDOWN_WEEKS = 4;

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

const promptStateSchema = z.object({
  lastPromptedAt: z.number().optional(),
  lastPasskeyLoginAt: z.number().optional(),
});

type PromptState = z.infer<typeof promptStateSchema>;

function getPromptStateStorageKey(userId: string): string {
  return `${PROMPT_STATE_STORAGE_KEY_PREFIX}${userId}`;
}

function getPromptState(userId: string): PromptState {
  try {
    const storedState = localStorage.getItem(getPromptStateStorageKey(userId));
    return storedState ? promptStateSchema.parse(JSON.parse(storedState)) : {};
  } catch {
    return {};
  }
}

function setPromptState(userId: string, state: PromptState): void {
  try {
    localStorage.setItem(getPromptStateStorageKey(userId), JSON.stringify(state));
  } catch {
    // localStorage may be unavailable.
  }
}

function isWithinCooldown(timestamp: number | undefined, now: Date): boolean {
  return (
    timestamp !== undefined && isAfter(addWeeks(new Date(timestamp), PROMPT_COOLDOWN_WEEKS), now)
  );
}

export function requestPasskeySetupPrompt(userId: string): void {
  try {
    sessionStorage.setItem(PENDING_PROMPT_STORAGE_KEY, userId);
  } catch {
    // sessionStorage may be unavailable.
  }
}

export function hasPendingPasskeySetupPrompt(userId: string): boolean {
  try {
    return sessionStorage.getItem(PENDING_PROMPT_STORAGE_KEY) === userId;
  } catch {
    return false;
  }
}

export function clearPendingPasskeySetupPrompt(): void {
  try {
    sessionStorage.removeItem(PENDING_PROMPT_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable.
  }
}

function getDeclinedCeremonyStorageKey(email: string): string {
  return `${DECLINED_CEREMONY_STORAGE_KEY_PREFIX}${normalizeEmail(email)}`;
}

/**
 * Records that a passkey ceremony ran for `email` this session and the user
 * dismissed it. Cognito only runs the WEB_AUTHN ceremony when the account has a
 * registered passkey, so a dismissal means the user has one and chose not to
 * use it — don't nag them to set one up after they sign in another way.
 */
export function recordPasskeyCeremonyDeclined(email: string): void {
  try {
    sessionStorage.setItem(getDeclinedCeremonyStorageKey(email), "true");
  } catch {
    // sessionStorage may be unavailable.
  }
}

export function hasDeclinedPasskeyCeremony(email: string): boolean {
  try {
    return sessionStorage.getItem(getDeclinedCeremonyStorageKey(email)) === "true";
  } catch {
    return false;
  }
}

export function shouldShowPasskeySetupPrompt(userId: string, now = new Date()): boolean {
  const state = getPromptState(userId);
  return (
    !isWithinCooldown(state.lastPromptedAt, now) && !isWithinCooldown(state.lastPasskeyLoginAt, now)
  );
}

export function recordPasskeySetupPromptShown(userId: string, now = new Date()): void {
  setPromptState(userId, { ...getPromptState(userId), lastPromptedAt: now.getTime() });
  clearPendingPasskeySetupPrompt();
}

export function recordPasskeyLogin(userId: string, now = new Date()): void {
  setPromptState(userId, { ...getPromptState(userId), lastPasskeyLoginAt: now.getTime() });
  clearPendingPasskeySetupPrompt();
}
